import { useState } from 'react';
import { useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import { getFormattedDate } from 'lib/common';
import {
  ContentLabel,
  EmptyIndicator,
  Pagination,
  Search,
  TimeIndicator,
  Tooltip,
  User
} from 'components/common';
import {
  Box,
  BoxOverflowWrapper,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import { UserDataAccessLogDetails } from '.';

import type { GetAccessLogsRes } from 'shared-types/responses';

const SearchField = ({
  label,
  onSearch,
  placeholder
}: {
  label: string;
  onSearch: (search: string) => void;
  placeholder?: string;
}) => (
  <div>
    <ContentLabel horizontal={true} label={label} horizontalSpacing={10}>
      <div style={{ width: 300 }}>
        <Search
          onSearch={({ search }) => onSearch(search)}
          placeholder={placeholder}
          size="header"
        />
      </div>
    </ContentLabel>
  </div>
);

const UserDataAccessLog = () => {
  const { language, messages, theme } = useReduxState();

  const [accessedBy, setAccessedBy] = useState('');
  const [affectedUser, setAffectedUser] = useState('');
  const [page, setPage] = useState(1);

  const { data, loading } = useApiData<GetAccessLogsRes>({
    url: `/admin/access-logs?accessedBy=${accessedBy}&affectedUser=${affectedUser}&page=${page}`
  });

  const handleSearch = (search: string, field: 'access' | 'affected') => {
    if (field === 'access') {
      setAccessedBy(search);
    } else if (field === 'affected') {
      setAffectedUser(search);
    }
    setPage(1);
  };

  return (
    <>
      <Box
        loading={loading}
        padding={0}
        subHeaderContent={
          <div className="search-fields">
            <SearchField
              label={messages.adminUserDataAccessLabelAccessBy}
              onSearch={(search) => handleSearch(search, 'access')}
              placeholder={messages.usersSearchUsers}
            />
            <SearchField
              label={messages.adminUserDataAccessLabelAffectedUser}
              onSearch={(search) => handleSearch(search, 'affected')}
              placeholder={messages.usersSearchUsers}
            />
          </div>
        }
        title={messages.adminUserDataAccessLog}
        titleDescription={messages.adminUserDataAccessDescription}
      >
        {data?.accessLogs?.length ? (
          <BoxOverflowWrapper>
            <BoxTable>
              <BoxTableHeader>
                <BoxTableCell>
                  {messages.adminUserDataAccessLabelAccessBy}
                </BoxTableCell>
                <BoxTableCell>{messages.commonTime}</BoxTableCell>
                <BoxTableCell>
                  {messages.adminUserDataAccessLabelAffectedUsers}
                </BoxTableCell>
                <BoxTableCell>
                  {messages.adminUserDataAccessLabelEndpoint}
                </BoxTableCell>
                <BoxTableCell shrink={true} />
              </BoxTableHeader>

              {data.accessLogs.map((log) => (
                <BoxTableRow key={log.id}>
                  <BoxTableCell>
                    {typeof log.requestUser === 'string' ? (
                      log.requestUser
                    ) : (
                      <User
                        enableProfileLink={true}
                        style={{ minWidth: 200 }}
                        user={log.requestUser}
                      />
                    )}
                  </BoxTableCell>
                  <BoxTableCell>
                    <Tooltip
                      content={getFormattedDate({
                        date: log.date,
                        dateFormat: 'dd.MM.y HH:mm'
                      })}
                    >
                      <TimeIndicator
                        language={language}
                        style={{
                          color: theme.textColorLighter,
                          fontWeight: 400,
                          whiteSpace: 'nowrap'
                        }}
                        timeString={log.date}
                      />
                    </Tooltip>
                  </BoxTableCell>
                  <BoxTableCell>{log.affectedUserIds.length || 0}</BoxTableCell>
                  <BoxTableCell>{log.route}</BoxTableCell>
                  <BoxTableCell>
                    <UserDataAccessLogDetails accessLog={log} />
                  </BoxTableCell>
                </BoxTableRow>
              ))}
            </BoxTable>
          </BoxOverflowWrapper>
        ) : (
          <EmptyIndicator
            icon="user"
            title={messages.adminUserDataAccessEmpty}
          />
        )}
      </Box>

      <Pagination
        currentPage={page || 1}
        numberOfPages={data?.pages || 1}
        onPageChange={({ page }) => setPage(page)}
      />

      <style jsx>{`
        .search-fields {
          align-items: center;
          display: flex;
          flex-wrap: wrap;
          gap: 20px;
          width: 100%;
        }
      `}</style>
    </>
  );
};

export default UserDataAccessLog;
